import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface CartItem {
  id: number
  name: string
  price: number
  quantity: number
  image: string
}

interface Product {
  id: number
  name: string
  price: number
  image: string
  brand?: string
}

interface StoreState {
  cart: CartItem[]
  wishlist: Product[]
  language: 'en' | 'ar'
  setLanguage: (lang: 'en' | 'ar') => void
  addToCart: (product: Product) => void
  removeFromCart: (productId: number) => void
  updateQuantity: (productId: number, quantity: number) => void
  toggleWishlist: (product: Product) => void
  isInWishlist: (productId: number) => boolean
  clearCart: () => void
  cartTotal: () => number
  cartCount: () => number
  searchQuery: string
  setSearchQuery: (query: string) => void
  recentSearches: string[]
  addRecentSearch: (query: string) => void
  clearRecentSearches: () => void
}

export const useStore = create<StoreState>()(
  persist(
    (set, get) => ({
      cart: [],
      wishlist: [],
      language: 'en',
      searchQuery: '',
      recentSearches: [],

      setLanguage: (lang) => set({ language: lang }),
      setSearchQuery: (query) => set({ searchQuery: query }),

      addRecentSearch: (query) => {
        const searches = get().recentSearches
        const filtered = searches.filter((s) => s !== query)
        set({ recentSearches: [query, ...filtered].slice(0, 5) })
      },

      clearRecentSearches: () => set({ recentSearches: [] }),

      addToCart: (product) => {
        const cart = get().cart
        const existingItem = cart.find((item) => item.id === product.id)

        if (existingItem) {
          set({
            cart: cart.map((item) =>
              item.id === product.id
                ? { ...item, quantity: item.quantity + 1 }
                : item,
            ),
          })
        } else {
          set({
            cart: [...cart, { ...product, quantity: 1 }],
          })
        }
      },

      removeFromCart: (productId) => {
        set({
          cart: get().cart.filter((item) => item.id !== productId),
        })
      },

      updateQuantity: (productId, quantity) => {
        if (quantity <= 0) {
          get().removeFromCart(productId)
          return
        }
        set({
          cart: get().cart.map((item) =>
            item.id === productId ? { ...item, quantity } : item,
          ),
        })
      },

      toggleWishlist: (product) => {
        const wishlist = get().wishlist
        const exists = wishlist.find((item) => item.id === product.id)

        if (exists) {
          set({
            wishlist: wishlist.filter((item) => item.id !== product.id),
          })
        } else {
          set({
            wishlist: [...wishlist, product],
          })
        }
      },

      isInWishlist: (productId) => {
        return get().wishlist.some((item) => item.id === productId)
      },

      clearCart: () => set({ cart: [] }),

      cartTotal: () => {
        return get().cart.reduce(
          (total, item) => total + item.price * item.quantity,
          0,
        )
      },

      cartCount: () => {
        return get().cart.reduce((total, item) => total + item.quantity, 0)
      },
    }),
    {
      name: 'innova-pharmacy-storage',
    },
  ),
)
